// src/applicants/ApplicantLayout.js
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  TablePagination,
  Autocomplete,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CloudUpload as UploadIcon,
  Public as CountryIcon,
  Work as ProfessionIcon,
  InsertDriveFile as InsertDriveFileIcon,
  VideoCall as VideoCallIcon,
} from '@mui/icons-material';
import HiringRoomApplicantImportModal from './HiringRoomApplicantImportModal';
import { CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import api from '../config/api';
import useStore from '../config/store';
import ApplicantModal from './ApplicantModal';
import ApplicantBulkUploadModal from './ApplicantBulkUploadModal';
import ApplicantInterviewsModal from './ApplicantInterviewsModal';
import { useTranslation } from 'react-i18next';

const ApplicantLayout = () => {
  const [applicants, setApplicants] = useState([]);
  const [filteredApplicants, setFilteredApplicants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [applicantToDelete, setApplicantToDelete] = useState(null);
  const [openBulkUploadModal, setOpenBulkUploadModal] = useState(false);
  const [hiringRoomConnected, setHiringRoomConnected] = useState(false);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [positions, setPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [openInterviewsModal, setOpenInterviewsModal] = useState(false);
  const [interviewsApplicant, setInterviewsApplicant] = useState(null);

  const { authToken, showAlert, user, project, organization, language } = useStore.getState();
  const { t } = useTranslation();

  dayjs.locale(language);

  const formatDate = (dateValue) => {
    if (!dateValue) return '';

    try {
      let date;

      if (dateValue && dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      } else if (dateValue && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else if (dateValue && dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (typeof dateValue === 'number') {
        date = new Date(dateValue);
      } else {
        return '';
      }

      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const getDateFromValue = (dateValue) => {
    if (!dateValue) return null;
    try {
      if (dateValue._seconds) return new Date(dateValue._seconds * 1000);
      if (typeof dateValue.toDate === 'function') return dateValue.toDate();
      if (dateValue.seconds) return new Date(dateValue.seconds * 1000);
      if (dateValue instanceof Date) return dateValue;
      if (typeof dateValue === 'string') return new Date(dateValue);
      if (typeof dateValue === 'number') return new Date(dateValue);
      return null;
    } catch {
      return null;
    }
  };

  const positionsFromApplicants = (() => {
    const map = {};
    applicants.forEach((applicant) => {
      applicant.positions?.forEach((p) => {
        if (p.positionId && p.positionName && !map[p.positionId]) {
          map[p.positionId] = p.positionName;
        }
      });
    });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  })();

  const filterApplicants = (applicants, searchTerm, filterPosition, startDate, endDate) => {
    let result = applicants;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((applicant) => {
        return (
          applicant.name?.toLowerCase().includes(term) ||
          applicant.email?.toLowerCase().includes(term) ||
          applicant.phone?.toLowerCase().includes(term) ||
          applicant.rut?.toLowerCase().includes(term)
        );
      });
    }

    if (filterPosition) {
      result = result.filter((applicant) =>
        applicant.positions?.some((p) => p.positionId === filterPosition)
      );
    }

    if (startDate) {
      const start = new Date(startDate + 'T00:00:00');
      result = result.filter((applicant) => {
        const modDate = getDateFromValue(applicant.modified);
        return modDate && modDate >= start;
      });
    }

    if (endDate) {
      const end = new Date(endDate + 'T23:59:59');
      result = result.filter((applicant) => {
        const modDate = getDateFromValue(applicant.modified);
        return modDate && modDate <= end;
      });
    }

    return result;
  };

  const checkHiringRoomConnection = async () => {
    try {
      const response = await api.get(
        '/projects/hiringroom-configuration',
        {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (response.data.response.status_response === 'OK') {
        const config = response.data.response.data;
        setHiringRoomConnected(config?.connected === true);
      }
    } catch (err) {
      console.error('Error verificando conexión HiringRoom:', err);
    }
  };

  useEffect(() => {
    if (authToken && organization && project) {
      checkHiringRoomConnection();
    }
  }, [authToken, organization, project]);

  const handleImportComplete = () => {
    loadApplicants();
  };

  useEffect(() => {
    const filtered = filterApplicants(applicants, searchTerm, filterPosition, startDate, endDate);
    filtered.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }));
    setFilteredApplicants(filtered);
    setPage(0);
  }, [applicants, searchTerm, filterPosition, startDate, endDate]);

  const clearSearch = () => {
    setSearchTerm('');
  };

  // organizationId (2026-09-07): suma los registros del BANCO DE TALENTO de la
  // organización (postulantes sin proyecto, captados por WhatsApp/web libre),
  // visibles en todos los proyectos de la org.
  const getAllApplicants = async () => {
    const response = await api.get(`/applicants`, {
      params: { projectId: project, organizationId: organization },
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  };

  const deleteApplicant = async (id) => {
    const response = await api.delete(`/applicants/${id}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  };

  const loadPositions = async () => {
    try {
      setLoadingPositions(true);
      const response = await api.get('/positions', {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.data.response && response.data.response.status_response === 'OK') {
        setPositions(response.data.response.data || []);
      }
    } catch (err) {
      console.error('Error loading positions:', err);
    } finally {
      setLoadingPositions(false);
    }
  };

  useEffect(() => {
    loadApplicants();
    loadPositions();
  }, []);

  const loadApplicants = async () => {
    try {
      setLoading(true);
      const response = await getAllApplicants();
      if (response.response && response.response.status_response === 'OK') {
        setApplicants(response.response.data || []);
      } else {
        setError(t('applicants.loadError'));
        showAlert(t('applicants.loadError'), "warning");
      }
    } catch (err) {
      setError(t('generalApiError'));
      showAlert(t('generalApiError'), "error");
      console.error('Error loading applicants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (mode, applicant = null) => {
    setModalMode(mode);
    setSelectedApplicant(applicant);
    setOpenModal(true);
    loadPositions();
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedApplicant(null);
    setError(null);
  };

  const handleSaveModal = () => {
    loadApplicants();
  };

  const handleOpenBulkUpload = () => {
    setOpenBulkUploadModal(true);
  };

  const handleCloseBulkUpload = () => {
    setOpenBulkUploadModal(false);
  };

  const handleBulkUploadSuccess = () => {
    loadApplicants();
  };

  const handleDelete = (applicant) => {
    setApplicantToDelete(applicant);
    setOpenDeleteDialog(true);
  };

  const handleOpenInterviews = (applicant) => {
    setInterviewsApplicant(applicant);
    setOpenInterviewsModal(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await deleteApplicant(applicantToDelete.id || applicantToDelete.uid);

      if (response.response && response.response.status_response === 'OK') {
        await loadApplicants();
        showAlert(t('applicants.deleteSuccess'), "success");
      } else {
        setError(t('applicants.deleteError'));
        showAlert(t('applicants.deleteError'), "error");
      }
    } catch (err) {
      setError(t('applicants.deleteError'));
      showAlert(t('applicants.deleteError'), "error");
      console.error('Error deleting applicant:', err);
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
      setApplicantToDelete(null);
    }
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setApplicantToDelete(null);
  };

  if (loading && applicants.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {t('applicants.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('applicants.subTitle')}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          {hiringRoomConnected && (
            <Button
              variant="outlined"
              startIcon={<CloudDownloadIcon />}
              onClick={() => setOpenImportModal(true)}
              sx={{
                borderColor: '#10b981',
                color: '#10b981',
                '&:hover': {
                  borderColor: '#059669',
                  backgroundColor: '#ecfdf5'
                }
              }}
            >
              {t('applicants.importWithHiringRoom')}
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handleOpenBulkUpload}
          >
            {t('applicants.bulkUpload')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal('create')}
          >
            {t('applicants.newApplicant')}
          </Button>
        </Box>
      </Box>

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                placeholder={t('applicants.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={positionsFromApplicants}
                getOptionLabel={(option) => option.name}
                value={positionsFromApplicants.find((p) => p.id === filterPosition) || null}
                onChange={(_, newValue) => setFilterPosition(newValue ? newValue.id : '')}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('applicants.positions')}
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language}>
                <DatePicker
                  label={t('dashboard.filters.startDate')}
                  value={startDate ? dayjs(startDate) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      setStartDate(newValue.format('YYYY-MM-DD'));
                    } else {
                      setStartDate('');
                    }
                  }}
                  format="YYYY-MM-DD"
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language}>
                <DatePicker
                  label={t('dashboard.filters.endDate')}
                  value={endDate ? dayjs(endDate) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      setEndDate(newValue.format('YYYY-MM-DD'));
                    } else {
                      setEndDate('');
                    }
                  }}
                  format="YYYY-MM-DD"
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6} md={2} display="flex" alignItems="center">
              <Button
                variant="outlined"
                size="small"
                fullWidth
                sx={{ height: 40 }}
                onClick={() => {
                  setSearchTerm('');
                  setFilterPosition('');
                  setStartDate('');
                  setEndDate('');
                }}
                disabled={!searchTerm && !filterPosition && !startDate && !endDate}
              >
                {t('applicants.clearFilters')}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && filteredApplicants.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {searchTerm
            ? `${t('applicants.noApplicantsFound')} "${searchTerm}"`
            : (filterPosition || startDate || endDate)
              ? t('applicants.noApplicantsMatchFilters')
              : t('applicants.noApplicants')
          }
        </Alert>
      )}

      {/* Tabla de postulantes */}
      <TableContainer
        component={Paper}
        sx={{
          maxWidth: '100%',
          overflowX: 'auto',
          maxHeight: 'calc(100vh - 330px)',
          overflowY: 'auto',
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('fullName')}</TableCell>
              <TableCell>{t('email')}</TableCell>
              <TableCell>{t('applicants.profession')}</TableCell>
              <TableCell>{t('applicants.country')}</TableCell>
              <TableCell>{t('applicants.positions')}</TableCell>
              <TableCell>CV</TableCell>
              <TableCell>{t('interviews.title')}</TableCell>
              <TableCell>{t('lastModified')}</TableCell>
              <TableCell
                align="center"
                sx={{
                  position: 'sticky',
                  right: 0,
                  backgroundColor: 'background.paper',
                  zIndex: 1,
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                }}
              >
                {t('actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredApplicants.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((applicant) => (
              <TableRow key={applicant.email || applicant.id || applicant.uid}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="action" fontSize="small" />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {applicant.name}
                      </Typography>
                      {applicant.hiringRoomApplicantId && (
                        <Chip
                          label={t('applicants.hiringRoomBadge')}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: '20px' }}
                        />
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="action" fontSize="small" />
                    {applicant.email ? (
                      <Chip
                        label={applicant.email}
                        variant="outlined"
                        size="small"
                        color="primary"
                      />
                    ) : (
                      // Ficha sin correo (PLAN_IMPORTACION_COMPUTRABAJO, fase 0):
                      // el que dio la persona está en otra ficha del proyecto
                      <Tooltip
                        title={applicant.pendingEmail
                          ? t('applicants.pendingEmailTooltip', { email: applicant.pendingEmail })
                          : t('applicants.noEmailChip')}
                      >
                        <Chip
                          label={t('applicants.noEmailChip')}
                          variant="outlined"
                          size="small"
                          color="warning"
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ProfessionIcon color="action" fontSize="small" />
                    <Typography variant="body2">
                      {applicant.profession || '-'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CountryIcon color="action" fontSize="small" />
                    <Typography variant="body2">
                      {applicant.country || '-'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {applicant.positions && applicant.positions.length > 0 ? (
                    <Tooltip
                      title={
                        <Box>
                          {applicant.positions.map((pos, idx) => (
                            <Typography key={idx} variant="body2">
                              • {pos.positionName}
                            </Typography>
                          ))}
                        </Box>
                      }
                      arrow
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer'
                        }}
                      >
                        {applicant.positions.map(p => p.positionName).join(', ')}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {applicant.cvUrl ? (
                    <IconButton
                      size="small"
                      onClick={() => window.open(applicant.cvUrl, '_blank')}
                      title={t('applicants.viewCv')}
                      color="primary"
                    >
                      <InsertDriveFileIcon />
                    </IconButton>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title={t('interviews.viewInterviews')}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenInterviews(applicant)}
                    >
                      <VideoCallIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(applicant.modified)}
                  </Typography>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    position: 'sticky',
                    right: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 1,
                    boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleOpenModal('view', applicant)}
                    title={t('show')}
                  >
                    <ViewIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenModal('edit', applicant)}
                    title={t('edit')}
                  >
                    <EditIcon />
                  </IconButton>
                  {user.rol === "super-admin" && (
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(applicant)}
                      title={t('delete')}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredApplicants.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        sx={{ mr: 6 }}
      />

      {/* Modal para crear/editar/ver */}
      <ApplicantModal
        open={openModal}
        onClose={handleCloseModal}
        mode={modalMode}
        selectedApplicant={selectedApplicant}
        onSave={handleSaveModal}
        user={user}
        authToken={authToken}
        showAlert={showAlert}
        positions={positions}
        loadingPositions={loadingPositions}
      />

      {/* Modal de carga masiva */}
      <ApplicantBulkUploadModal
        open={openBulkUploadModal}
        onClose={handleCloseBulkUpload}
        onSuccess={handleBulkUploadSuccess}
      />

      {/* Modal de confirmación para eliminar */}
      <Dialog open={openDeleteDialog} onClose={cancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('applicants.deleteTitle')}
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1">
            {t('applicants.applicantDeleteConfirm')} <strong>"{applicantToDelete?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('noWayBack')}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={cancelDelete} color="inherit">
            {t('cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de importación de HiringRoom */}
      <HiringRoomApplicantImportModal
        open={openImportModal}
        onClose={() => setOpenImportModal(false)}
        onImportComplete={handleImportComplete}
      />

      {/* Modal de entrevistas */}
      <ApplicantInterviewsModal
        open={openInterviewsModal}
        onClose={() => setOpenInterviewsModal(false)}
        applicant={interviewsApplicant}
      />
    </Box>
  );
};

export default ApplicantLayout;